import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import ExoTable from '../../../components/exo/ExoTable';
import { useTranslation } from 'react-i18next';
import { Status } from '../../../components/special/Status';
import { Box, Typography } from '@mui/material';
import ExoAvatar from '../../../components/exo/ExoAvatar';
import { LeadStatusSelector } from '../status/LeadStatusSelector';
import { API_PATCH, DBRequest, useConfig } from '../../../api/api';
import { updateOrCreate } from '../../../components/special/updateOrCreate';
import { useNavigate } from 'react-router-dom';
import DateLabel from '../../../components/label/DateLabel';
import Cookies from 'universal-cookie';
import { TableCategories } from '../category/TableCategories';

export const LeadsCompanies = forwardRef(({ isActive }, ref) => {
  const config = useConfig();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const cookies = new Cookies();
  const [apiData, setApiData] = useState(null);

  useEffect(() => {
    const currentLeadsFilter = cookies.get('leads');

    DBRequest({
      config,
      path: currentLeadsFilter
        ? `leads/companies?${currentLeadsFilter}`
        : 'leads/companies',
      onResponse: setApiData,
    });

    DBRequest({
      config,
      path: `lead-statuses`,
      onResponse: calcFilters,
    });
  }, [config]);

  const [anchorEl, setAnchorEl] = useState(null);
  const [itemToChange, setItemToChange] = useState(null);
  const handleClick = (event, item) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
    setItemToChange(item);
  };
  function handleStatusChange(status, item) {
    DBRequest({
      config,
      path: `leads/companies/${item.id}`,
      method: API_PATCH({ ...item, status }),
      onResponse: (data) => setApiData(updateOrCreate(apiData, data.data)),
    });
  }

  const columns = [
    {
      header: t('Status'), // header of column
      key: 'status', // key of column in row-object
      thAlign: 'left', // header text alignment
      tdAlign: 'left', // cell text alignment
      sortable: true, // enable sort function for column
      cell: ({ row }) => (
        <Status
          text={row.status?.label || t('No Status')}
          variant={row.status?.variant || 'base'}
          statusColor={row.status?.color}
          onClick={(e) => handleClick(e, { id: row.id })}
        />
      ),
    },
    {
      header: t('Categories'), // header of column
      key: 'categories', // key of column in row-object
      thAlign: 'left', // header text alignment
      tdAlign: 'left', // cell text alignment
      width: '115px',
      cell: ({ row }) => <TableCategories categories={row.categories} />,
      hiddenMobile: true,
    },
    {
      header: t('Company Name'), // header of column
      key: 'name', // key of column in row-object
      thAlign: 'left', // header text alignment
      tdAlign: 'left', // cell text alignment
      sortable: true, // enable sort function for column
      width: '100%',
      cell: ({ row }) => (
        <Box className="flex flex-row justify-start items-center gap-2 ">
          <ExoAvatar
            picture={row.logo && row.logo.thumbnailUrl}
            name={row.nameShort || row.name || ''}
            size="35px"
            type="company"
          />
          {row.nameShort || row.name}
        </Box>
      ),
      onClick: (row) => navigate(`/leads/companies/${row.id}`),
    },
    {
      header: t('Last Interaction'), // header of column
      key: 'lastInteractionDate', // key of column in row-object
      thAlign: 'left', // header text alignment
      tdAlign: 'left', // cell text alignment
      sortable: true, // enable sort function for column
      cell: ({ row }) => {
        const date = row.lastInteractionDate;
        return (
          <Box className="flex flex-row justify-start items-center gap-2">
            {date ? <DateLabel date={date} /> : t('no contact')}
          </Box>
        );
      },
      onClick: (row) => navigate(`/leads/persons/${row.id}`),
      hiddenMobile: true,
    },
    {
      header: t('Responsible Employee'), // header of column
      key: 'responsibleUser', // key of column in row-object
      thAlign: 'left', // header text alignment
      tdAlign: 'left', // cell text alignment
      sortable: true, // enable sort function for column
      cell: ({ row }) => (
        <Box className="flex flex-row justify-start items-center gap-2 whitespace-nowrap text-ellipsis">
          <ExoAvatar
            picture={row.responsibleUser?.picture?.thumbnailUrl}
            name={`${row.responsibleUser?.lastName} ${row.responsibleUser?.firstName}`}
            size="35px"
          />
          {`${row.responsibleUser?.lastName} ${row.responsibleUser?.firstName}`}
        </Box>
      ),
      hiddenMobile: true,
    },
    {
      header: t('E-Mail'),
      key: 'emails',
      thAlign: 'left',
      tdAlign: 'left',
      cell: ({ row }) => {
        const primaryEmail = row.emails?.find((e) => e.isPrimary);
        const email =
          primaryEmail?.emailAddress || row.emails?.[0]?.emailAddress;
        return email ? (
          <a
            href={`mailto:${email}`}
            className="text-blue-500 hover:underline"
          >
            {email}
          </a>
        ) : (
          '-'
        );
      },
      hiddenMobile: true,
    },
    {
      header: t('URL'),
      key: 'url',
      thAlign: 'left',
      tdAlign: 'left',
      cell: ({ row }) =>
        row.url ? (
          <a
            href={row.url.startsWith('http') ? row.url : `https://${row.url}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-500 hover:underline"
          >
            {row.url}
          </a>
        ) : (
          '-'
        ),
      hiddenMobile: true,
    },
    {
      header: t('Note'),
      key: 'note',
      thAlign: 'left',
      tdAlign: 'left',
      cell: ({ row }) => (
        <Typography
          className="max-w-[200px] truncate"
          title={row.note}
        >
          {row.note || '-'}
        </Typography>
      ),
      hiddenMobile: true,
    },
    {
      header: t('Employee Count'),
      key: 'employeeCount',
      thAlign: 'right',
      tdAlign: 'right',
      sortable: true,
      cell: ({ row }) => row.employeeCount ?? '-',
      hiddenMobile: true,
    },
    {
      header: t('Address'),
      key: 'address',
      thAlign: 'left',
      tdAlign: 'left',
      cell: ({ row }) => {
        const a = row.address;
        if (!a) return '-';
        const street = [a.street, a.houseNumber].filter(Boolean).join(' ');
        const city = [a.zipCode, a.city].filter(Boolean).join(' ');
        return [street, city].filter(Boolean).join(', ') || '-';
      },
      hiddenMobile: true,
    },
    {
      header: t('City'),
      key: 'addressCity',
      thAlign: 'left',
      tdAlign: 'left',
      cell: ({ row }) => row.address?.city || '-',
      hiddenMobile: true,
    },
    {
      header: t('Country'),
      key: 'addressCountry',
      thAlign: 'left',
      tdAlign: 'left',
      cell: ({ row }) => row.address?.country || '-',
      hiddenMobile: true,
    },
  ];
  // exposed functions
  const tableRef = useRef(null);
  useImperativeHandle(ref, () => ({
    setApiData: setApiData,
    addRow: handleCreate,
    addRows: (newRows) =>
      setApiData({
        ...apiData,
        data: [...apiData.data, ...newRows.data],
      }),
    updateRow: handleCreate,
    deleteRow: handleDelete,
  }));

  function handleCreate(data) {
    if (!tableRef) return;
    tableRef.current.addRow(data);
  }
  function handleDelete(data) {
    if (!tableRef) return;
    tableRef.current.deleteRow(data);
  }

  // filter stuff

  const [filters, setFilters] = useState({
    cookieKey: 'leads',
    filter: {
      label: t('Filter'),
      items: [
        {
          type: 'dropdown',
          label: t('Status'),
          key: 'state2',
          baseValue: 'statusId[eq]=',
          active: false,
          options: [
            {
              label: t('Sent'),
              value: 0,
            },
            {
              label: t('Accepted'),
              value: 1,
            },
            {
              label: t('Finished'),
              value: 2,
            },
            {
              label: t('Rejected'),
              value: 3,
            },
            {
              label: t('Created'),
              value: 4,
            },
          ],
        },
      ],
    },
  });

  function calcFilters(res) {
    var options = [];
    res?.data.forEach((element) => {
      options.push({ label: element.label, value: element.id });
    });

    const newFilters = {
      cookieKey: 'leads',
      filter: {
        label: t('Filter'),
        items: [
          {
            type: 'dropdown',
            label: t('Status'),
            key: 'state2',
            baseValue: 'statusId[eq]=',
            active: false,
            options,
          },
          ///*
          {
            type: 'leadCategory',
            label: t('Category'),
            key: 'categoryIds',
            baseValue: 'categoryIds[]=',
            active: false,
          }, //*/
        ],
      },
    };

    setFilters(newFilters);
  }

  if (!isActive) return;
  return (
    <>
      <ExoTable
        ref={tableRef}
        data={apiData}
        isLoading={Boolean(!apiData)}
        columns={columns}
        filters={filters}
        columnSettingsKey="leads-companies"
        defaultHiddenColumns={['emails', 'url', 'note', 'employeeCount', 'address', 'addressCity', 'addressCountry']}
      />
      <LeadStatusSelector
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        onStatusChange={(status) => handleStatusChange(status, itemToChange)}
      />
    </>
  );
});
